import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, AlertTriangle, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';

/**
 * Modal específico para criar Multa de Trânsito.
 *
 * Diferente do form genérico de "Nova Cobrança", este foca em:
 *  - Valor + descrição da multa
 *  - Modo de cobrança (à vista / parcelado) + nº de parcelas
 *  - Anexo (PDF ou imagem) — opcional, salvo em R2
 *
 * Ao submeter, notifica o cliente via Web Push automaticamente.
 */
const MultaTransitoModal = ({ locacoes = [], locacaoInicial = null, onClose, onSucesso }) => {
  const [locacaoId, setLocacaoId] = useState(locacaoInicial?.id || '');
  const [valor, setValor] = useState('');
  const [descricaoMulta, setDescricaoMulta] = useState('');
  const [modoCobranca, setModoCobranca] = useState('avista'); // avista | parcelado
  const [parcelasTotal, setParcelasTotal] = useState(2);
  const [dataVencimento, setDataVencimento] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().split('T')[0];
  });
  const [observacoes, setObservacoes] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (locacaoInicial?.id) setLocacaoId(locacaoInicial.id);
  }, [locacaoInicial]);

  const TIPOS_MULTA = [
    'Multa Leve',
    'Multa Média',
    'Multa Grave',
    'Multa Gravíssima',
    'Excesso de velocidade',
    'Avanço de sinal vermelho',
    'Estacionamento irregular',
    'Sem cinto de segurança',
    'Outros',
  ];

  const handleSelecionarArquivo = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setArquivo(null); return; }
    const tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!tiposPermitidos.includes(f.type)) {
      setErro('Arquivo deve ser PDF, JPG ou PNG');
      e.target.value = '';
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setErro('Arquivo muito grande (máx 10 MB)');
      e.target.value = '';
      return;
    }
    setErro('');
    setArquivo(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!locacaoId) { setErro('Selecione a locação'); return; }
    const v = parseFloat(valor);
    if (!v || v <= 0) { setErro('Informe um valor válido'); return; }
    if (!descricaoMulta.trim()) { setErro('Informe o tipo / descrição da multa'); return; }
    if (modoCobranca === 'parcelado') {
      const n = parseInt(parcelasTotal);
      if (!n || n < 2) { setErro('Para parcelado informe quantidade ≥ 2'); return; }
    }

    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('locacao_id', locacaoId);
      fd.append('valor', String(v));
      fd.append('descricao_multa', descricaoMulta);
      fd.append('modo_cobranca', modoCobranca);
      if (modoCobranca === 'parcelado') fd.append('parcelas_total', String(parcelasTotal));
      if (dataVencimento) fd.append('data_vencimento', dataVencimento);
      if (observacoes) fd.append('observacoes', observacoes);
      if (arquivo) fd.append('arquivo', arquivo);

      await apiService.api.post('/admin/multas-transito', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSucesso?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Erro ao criar multa';
      setErro(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-red-500/30 bg-gradient-to-b from-gray-900 to-gray-950 shadow-2xl"
        data-testid="modal-multa-transito"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-red-500/20 bg-gradient-to-r from-red-900/30 via-red-800/20 to-transparent">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg border border-red-500/40">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Nova Multa de Trânsito</h2>
              <p className="text-xs text-red-300/70">Cliente será notificado por push</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="modal-multa-fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {/* Locação */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Locação *</label>
            <select
              value={locacaoId}
              onChange={(e) => setLocacaoId(e.target.value)}
              required
              disabled={!!locacaoInicial}
              data-testid="multa-locacao-select"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none"
            >
              <option value="">— Selecione —</option>
              {locacoes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.cliente_nome || l.cliente_id} {l.moto_placa ? `• ${l.moto_placa}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Tipo / Descrição */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tipo da Multa *</label>
            <input
              type="text"
              list="tipos-multa-list"
              value={descricaoMulta}
              onChange={(e) => setDescricaoMulta(e.target.value)}
              placeholder="Ex: Multa Grave - Excesso de velocidade"
              required
              data-testid="multa-descricao-input"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none"
            />
            <datalist id="tipos-multa-list">
              {TIPOS_MULTA.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Valor (R$) *</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="0.00"
              required
              data-testid="multa-valor-input"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none tabular-nums"
            />
          </div>

          {/* Modo: Avista / Parcelado */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Modo de Cobrança *</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setModoCobranca('avista')}
                data-testid="multa-modo-avista"
                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all ${
                  modoCobranca === 'avista'
                    ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                À Vista
              </button>
              <button
                type="button"
                onClick={() => setModoCobranca('parcelado')}
                data-testid="multa-modo-parcelado"
                className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all ${
                  modoCobranca === 'parcelado'
                    ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                Parcelado
              </button>
            </div>
            {modoCobranca === 'parcelado' && (
              <div className="mt-2">
                <label className="block text-xs text-gray-400 mb-1">Nº de parcelas (semanais)</label>
                <input
                  type="number"
                  min="2"
                  max="24"
                  value={parcelasTotal}
                  onChange={(e) => setParcelasTotal(parseInt(e.target.value) || 2)}
                  data-testid="multa-parcelas-input"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none tabular-nums"
                />
                {valor && parseFloat(valor) > 0 && (
                  <p className="text-[11px] text-purple-300 mt-1">
                    {parcelasTotal}x de R$ {(parseFloat(valor) / parcelasTotal).toFixed(2).replace('.', ',')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Data de Vencimento */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              {modoCobranca === 'parcelado' ? 'Vencimento da 1ª parcela' : 'Data de Vencimento'}
            </label>
            <input
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              data-testid="multa-vencimento-input"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              {modoCobranca === 'parcelado'
                ? 'Parcelas seguintes vencerão a cada 7 dias, alinhadas ao pagamento semanal.'
                : 'Se em branco, será definido para 3 dias após o cadastro.'}
            </p>
          </div>

          {/* Anexo */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Anexo (PDF ou imagem) — opcional</label>
            <label
              className="flex flex-col items-center justify-center gap-2 px-3 py-4 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-red-500/50 hover:bg-red-500/5 transition-colors"
              data-testid="multa-arquivo-dropzone"
            >
              {arquivo ? (
                <>
                  <FileText className="h-6 w-6 text-emerald-400" />
                  <span className="text-xs text-emerald-300 font-medium truncate max-w-full">{arquivo.name}</span>
                  <span className="text-[10px] text-gray-500">{(arquivo.size / 1024).toFixed(0)} KB</span>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-gray-500" />
                  <span className="text-xs text-gray-400">Toque para selecionar PDF / JPG / PNG</span>
                  <span className="text-[10px] text-gray-600">Máx 10 MB</span>
                </>
              )}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/jpg,image/png"
                onChange={handleSelecionarArquivo}
                className="hidden"
                data-testid="multa-arquivo-input"
              />
            </label>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Observações (opcional)</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={2}
              placeholder="Ex: Auto de infração nº 123456..."
              data-testid="multa-observacoes-input"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-red-500 focus:outline-none resize-none"
            />
          </div>

          {/* Erro */}
          {erro && (
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="multa-erro">
              {erro}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={enviando}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:bg-white/5 transition-colors text-sm font-medium"
              data-testid="multa-cancelar"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={enviando}
              data-testid="multa-submit"
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-sm font-semibold shadow-lg shadow-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {enviando ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>
              ) : (
                <>🚨 Cadastrar Multa</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MultaTransitoModal;
